import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Monitor, Minimize2, Maximize2, Camera, X, RefreshCw, Video, VideoOff } from 'lucide-react';
import { Room, RoomEvent, Track } from 'livekit-client';
import styles from './DesktopViewer.module.css';

interface DesktopViewerProps {
  userId: number;
  onClose?: () => void;
  minimized?: boolean;
}

export function DesktopViewer({ userId, onClose, minimized: initialMinimized }: DesktopViewerProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMinimized, setIsMinimized] = useState(initialMinimized ?? false);
  const [isPolling, setIsPolling] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLiveStream, setIsLiveStream] = useState(false);
  const [streamConnecting, setStreamConnecting] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const roomRef = useRef<Room | null>(null);

  // ── LiveKit screen share ──────────────────────────────────
  const [agentId, setAgentId] = useState<string | null>(null);

  const connectLiveStream = useCallback(async () => {
    if (roomRef.current) return;
    setStreamConnecting(true);
    try {
      // Use agent ID from status endpoint for room name
      const aid = agentId || String(userId);
      const roomName = `desktop-share-${aid.slice(0, 8)}`;
      const res = await fetch('/_api/livekit/token', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomName,
          participantIdentity: `viewer-${userId}-${Date.now()}`,
          participantName: 'Mobile Viewer',
        }),
      });
      if (!res.ok) throw new Error('Failed to get LiveKit token');
      const data = await res.json() as any;
      const token = data.token || data.json?.token;
      const url = data.url || data.json?.url;
      if (!token || !url) throw new Error('Missing token/url');

      const room = new Room();
      roomRef.current = room;

      // Listen for screen share tracks
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Video && videoRef.current) {
          track.attach(videoRef.current);
          setIsLiveStream(true);
          setIsPolling(false); // Stop polling when live stream is active
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        if (track.kind === Track.Kind.Video) {
          track.detach();
          setIsLiveStream(false);
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        setIsLiveStream(false);
        roomRef.current = null;
      });

      await room.connect(url, token);
      setIsConnected(true);
    } catch (e: any) {
      console.warn('[DesktopViewer] LiveKit connect failed:', e.message);
      setError('Live stream unavailable — using screenshots');
    } finally {
      setStreamConnecting(false);
    }
  }, [userId]);

  const disconnectLiveStream = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    setIsLiveStream(false);
  }, []);

  // Auto-connect to live stream when agent is connected
  useEffect(() => {
    console.log('[DesktopViewer] State:', { isConnected, agentId, isLiveStream, hasRoom: !!roomRef.current, streamConnecting });
    if (isConnected && agentId && !isLiveStream && !roomRef.current && !streamConnecting) {
      console.log('[DesktopViewer] Auto-connecting to LiveKit...');
      connectLiveStream();
    }
  }, [isConnected, agentId, isLiveStream, streamConnecting, connectLiveStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { disconnectLiveStream(); };
  }, [disconnectLiveStream]);

  // ── Screenshot polling ──────────────────────────────────
  const fetchLatestScreenshot = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    try {
      const res = await fetch('/_api/taskingbot/desktop-screenshot', {
        credentials: 'include',
        signal: abortRef.current.signal,
      });
      if (res.ok) {
        const data = await res.json() as any;
        if (data.status === 'ok' || data.connected) {
          setIsConnected(true);
          setLastUpdated(new Date().toLocaleTimeString());
          setError(null);
          if (data.agentId || data.agent_id) setAgentId(data.agentId || data.agent_id);
          if (data.url) setImageUrl(data.url + '?t=' + Date.now());
        } else if (data.status === 'no_agent') {
          setIsConnected(false);
          setError('Desktop agent not connected');
        }
      } else {
        setIsConnected(false);
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        setIsConnected(false);
      }
    }
  }, []);

  const requestScreenshot = useCallback(async () => {
    try {
      await fetch('/_api/taskingbot/desktop-screenshot', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'capture' }),
      });
      // Wait a moment for capture, then fetch
      setTimeout(fetchLatestScreenshot, 1500);
    } catch {}
  }, [fetchLatestScreenshot]);

  useEffect(() => {
    if (isPolling && !isMinimized) {
      fetchLatestScreenshot();
      intervalRef.current = setInterval(fetchLatestScreenshot, 3000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [isPolling, isMinimized, fetchLatestScreenshot]);

  if (isMinimized) {
    return (
      <button
        className={styles.minimizedButton}
        onClick={() => setIsMinimized(false)}
        title="Show desktop view"
      >
        <Monitor size={16} />
        <span className={`${styles.statusDot} ${isConnected ? styles.connected : styles.disconnected}`} />
      </button>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Monitor size={14} />
          <span className={styles.title}>Desktop</span>
          <span className={`${styles.statusDot} ${isConnected ? styles.connected : styles.disconnected}`} />
          {lastUpdated && <span className={styles.timestamp}>{lastUpdated}</span>}
        </div>
        <div className={styles.headerActions}>
          <button className={styles.headerBtn} onClick={requestScreenshot} title="Capture now">
            <Camera size={13} />
          </button>
          <button
            className={styles.headerBtn}
            onClick={isLiveStream ? disconnectLiveStream : connectLiveStream}
            title={isLiveStream ? 'Disconnect live stream' : 'Connect live screen share'}
            disabled={streamConnecting}
            style={isLiveStream ? { color: 'var(--success, #22c55e)' } : undefined}
          >
            {isLiveStream ? <Video size={13} /> : <VideoOff size={13} />}
          </button>
          <button
            className={styles.headerBtn}
            onClick={() => setIsPolling(!isPolling)}
            title={isPolling ? 'Pause auto-refresh' : 'Resume auto-refresh'}
          >
            <RefreshCw size={13} className={isPolling ? styles.spinning : ''} />
          </button>
          <button className={styles.headerBtn} onClick={() => setIsMinimized(true)} title="Minimize">
            <Minimize2 size={13} />
          </button>
          {onClose && (
            <button className={styles.headerBtn} onClick={onClose} title="Close">
              <X size={13} />
            </button>
          )}
        </div>
      </div>
      <div className={styles.viewport}>
        {isLiveStream ? (
          <video
            ref={videoRef}
            className={styles.screenshot}
            autoPlay
            playsInline
            muted
          />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="Desktop screenshot"
            className={styles.screenshot}
            draggable={false}
          />
        ) : (
          <div className={styles.placeholder}>
            {streamConnecting ? 'Connecting live stream...' : error || (isConnected ? '✅ Desktop agent connected' : 'Desktop agent not connected')}
          </div>
        )}
      </div>
    </div>
  );
}

/** Desktop viewer that listens for toggle event from chat input */
export function DesktopViewerToggle({ userId }: { userId: number }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handler = () => setIsOpen((prev) => !prev);
    window.addEventListener('tasking.toggleDesktopViewer', handler);
    return () => window.removeEventListener('tasking.toggleDesktopViewer', handler);
  }, []);

  if (!isOpen) return null;

  return <DesktopViewer userId={userId} onClose={() => setIsOpen(false)} />;
}
